import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import {
  Avatar, Box, Button, Chip, FormControl, InputLabel, MenuItem, Paper, Select,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
  Typography, CircularProgress, Alert,
} from '@mui/material';
import firebase from '../store/firebase';

/*
 * Reads the precomputed leaderboard written by the m188-leaderboard Cloud Functions:
 *   leaderboard/{companyID}/{userID} = { firstName, lastName, avatar, sector, country,
 *                                        position, certs: [{ t, l, c }], total, topLevel }
 * Certificate rules and thresholds live in the backend (modules/certificateEngine.js).
 */

const LEVELS = ['Foundational', 'Advanced', 'Distinguished', 'Elite', 'Pinnacle']; // index+1 = level
const SECTION_ORDER = [...LEVELS].reverse(); // Pinnacle on top
const SECTION_PAGE_SIZE = 25;

// header = section divider row (grey, darkest at top)
// row    = people rows in that section (blue, darkest at top)
// Every shade keeps white text at WCAG AA contrast (≥ 4.5:1).
const LEVEL_COLORS = {
  Pinnacle:      { header: '#1f2328', row: '#0b1f4d' },
  Elite:         { header: '#33383f', row: '#12347a' },
  Distinguished: { header: '#454b54', row: '#1a4aa6' },
  Advanced:      { header: '#565d67', row: '#2462c9' },
  Foundational:  { header: '#666d77', row: '#3473dd' },
};
// Column-heading row: deep navy, darker than the Pinnacle divider beneath it
const COLUMN_HEADER_BG = '#060f26';

// Top-ranked rows (of whatever filter is applied): larger, yellow text
const TOP_N = 3;
const TOP_TEXT = '#fde047'; // readable on every section blue at this size

/* ------------------------------------------------------------------ */
/* Pure helpers                                                        */
/* ------------------------------------------------------------------ */

// RTDB may return a stored array as an object; normalise to the shape the UI uses
const toCerts = (stored) => Object.values(stored || {})
  .filter((c) => c && c.t && c.l)
  .map((c) => ({ title: c.t, level: Number(c.l), category: c.c || null }));

// counts[0] = Pinnacle ... counts[4] = Foundational
const countByLevel = (certs) => {
  const counts = [0, 0, 0, 0, 0];
  certs.forEach((c) => { counts[5 - c.level] += 1; });
  return counts;
};

// Lexicographic: most Pinnacle wins, then Elite, ... then total, then name.
const compareRows = (a, b) => {
  for (let i = 0; i < 5; i++) {
    if (b.counts[i] !== a.counts[i]) return b.counts[i] - a.counts[i];
  }
  if (b.total !== a.total) return b.total - a.total;
  return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
};

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

function CertificateLeaderboard({ userdata, reAuthKey }) {
  const [companies, setCompanies] = useState([]);           // [{ id, name }]
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPosition, setSelectedPosition] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataVersion, setDataVersion] = useState(0);         // bumps when cache fills
  const [expanded, setExpanded] = useState({});              // { Pinnacle: true, ... }

  // Loaded entries — filter changes never re-hit Firebase.
  // all = { companyID: { userID: entry } }, companies[cid] = { userID: entry }
  const cacheRef = useRef({ reAuthKey: null, all: null, companies: {} });

  // Rows are keyed by userID (the userTestResults key), so match on that
  const currentUid = userdata?.userID || null;

  /* ---- Load companies -------------------------------------------- */
  useEffect(() => {
    if (!userdata) return;
    firebase.database.ref('active-companies').once('value')
      .then((snap) => {
        const comps = [];
        snap.forEach((child) => {
          const val = child.val();
          if (val?.companyID && val?.companyName) comps.push({ id: val.companyID, name: val.companyName });
        });
        comps.sort((a, b) => a.name.localeCompare(b.name));
        setCompanies(comps);
      })
      .catch((err) => {
        console.error('Error loading companies:', err);
        setError('Could not load companies.');
      });
  }, [userdata, reAuthKey]);

  /* ---- Load precomputed entries for the selected scope ------------ */
  useEffect(() => {
    if (!userdata || companies.length === 0) return undefined;
    let cancelled = false;
    const cache = cacheRef.current;

    // Fresh auth → drop anything cached under the old session
    if (cache.reAuthKey !== reAuthKey) {
      cache.reAuthKey = reAuthKey;
      cache.all = null;
      cache.companies = {};
    }

    // "All companies" already loaded covers any single company too
    const haveIt = selectedCompany === 'all'
      ? !!cache.all
      : !!(cache.all || cache.companies[selectedCompany]);
    if (haveIt) {
      setDataVersion((v) => v + 1);
      setLoading(false);
      return undefined;
    }

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        if (selectedCompany === 'all') {
          const snap = await firebase.database.ref('leaderboard').once('value');
          cache.all = snap.val() || {};
        } else {
          const snap = await firebase.database.ref(`leaderboard/${selectedCompany}`).once('value');
          cache.companies[selectedCompany] = snap.val() || {};
        }
      } catch (err) {
        console.error('Error loading leaderboard:', err);
        if (!cancelled) setError('Could not load the leaderboard.');
      }
      if (!cancelled) {
        setDataVersion((v) => v + 1);
        setLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [userdata, companies, selectedCompany, reAuthKey]);

  /* ---- Base rows: everyone in the selected company scope ----------- */
  const baseRows = useMemo(() => {
    const cache = cacheRef.current;
    const nameById = Object.fromEntries(companies.map((c) => [c.id, c.name]));

    // { companyID: { userID: entry } } for the current scope
    let scope = {};
    if (selectedCompany === 'all') {
      scope = cache.all || {};
    } else {
      const fromAll = cache.all && cache.all[selectedCompany];
      scope = { [selectedCompany]: fromAll || cache.companies[selectedCompany] || {} };
    }

    const rows = [];
    Object.entries(scope).forEach(([cid, users]) => {
      if (!nameById[cid]) return; // only active companies, as before
      Object.entries(users || {}).forEach(([userID, e]) => {
        if (!e) return;
        rows.push({
          key: `${cid}:${userID}`,
          uid: userID,
          companyID: cid,
          company: nameById[cid],
          allCerts: toCerts(e.certs),
          firstName: e.firstName || '',
          lastName: e.lastName || '',
          avatar: e.avatar || '',
          sector: e.sector || '',
          country: e.country || '',
          position: e.position || '',
        });
      });
    });
    return rows;
  }, [dataVersion, companies, selectedCompany]);

  /* ---- Filter options (derived from the company scope) ------------ */
  const categoryOptions = useMemo(() => {
    const set = new Set();
    baseRows.forEach((r) => r.allCerts.forEach((c) => { if (c.category) set.add(c.category); }));
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [baseRows]);

  const positionOptions = useMemo(() => {
    const set = new Set();
    baseRows.forEach((r) => { if (r.position) set.add(r.position); });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [baseRows]);

  // Reset a filter if its value vanished after switching company
  useEffect(() => {
    if (selectedCategory !== 'all' && !categoryOptions.includes(selectedCategory)) setSelectedCategory('all');
  }, [categoryOptions, selectedCategory]);
  useEffect(() => {
    if (selectedPosition !== 'all' && !positionOptions.includes(selectedPosition)) setSelectedPosition('all');
  }, [positionOptions, selectedPosition]);

  /* ---- Ranked + sectioned rows ------------------------------------ */
  const { sections, rankedCount } = useMemo(() => {
    const ranked = baseRows
      .filter((r) => selectedPosition === 'all' || r.position === selectedPosition)
      .map((r) => {
        // Category filter: only certs tied to that category count.
        // Cross-category "Leadership - X" certs have no single category, so they drop out.
        const certs = selectedCategory === 'all'
          ? r.allCerts
          : r.allCerts.filter((c) => c.category === selectedCategory);
        const counts = countByLevel(certs);
        const highestIdx = counts.findIndex((n) => n > 0); // 0 = Pinnacle
        return { ...r, certs, counts, total: certs.length, highest: SECTION_ORDER[highestIdx] };
      })
      .filter((r) => r.total > 0)
      .sort(compareRows)
      .map((r, i) => ({ ...r, rank: i + 1 }));

    const grouped = Object.fromEntries(SECTION_ORDER.map((lvl) => [lvl, []]));
    ranked.forEach((r) => grouped[r.highest].push(r));
    return { sections: grouped, rankedCount: ranked.length };
  }, [baseRows, selectedCategory, selectedPosition]);

  /* ---- Render ------------------------------------------------------ */
  if (!userdata) return <Box sx={{ p: 2 }}>Please sign in.</Box>;

  const scopeLabel = [
    selectedCategory !== 'all' ? selectedCategory : 'All categories',
    selectedCompany !== 'all' ? companies.find((c) => c.id === selectedCompany)?.name : 'All companies',
    selectedPosition !== 'all' ? selectedPosition : null,
  ].filter(Boolean).join(' · ');

  const hideOnMobile = { display: { xs: 'none', md: 'table-cell' } };
  const COLS = 8;

  // Compact, single-line rows everywhere (mobile + desktop)
  const HEADER_H = 26; // px — sticky section headers sit right under the column header
  const tableSx = {
    '& .MuiTableCell-root': {
      whiteSpace: 'nowrap',
      py: '2px',
      px: { xs: 0.75, md: 1 },
      fontSize: { xs: 12, md: 13 },
      lineHeight: 1.25,
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      height: HEADER_H, fontWeight: 700, color: '#fff',
      bgcolor: COLUMN_HEADER_BG, borderBottom: 'none',
    },
  };
  // Long text: one line, ellipsis, full value on hover
  const truncate = (maxWidth) => ({ maxWidth, overflow: 'hidden', textOverflow: 'ellipsis' });
  const textCell = (value, maxWidth, extraSx) => (
    <TableCell sx={{ ...truncate(maxWidth), ...extraSx }} title={value || ''}>
      {value || '—'}
    </TableCell>
  );

  const renderFilter = (label, value, onChange, options, allLabel, getKey = (o) => o, getLabel = (o) => o) => (
    <FormControl size="small" sx={{ minWidth: 200, flex: '1 1 200px' }} disabled={loading}>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value} onChange={(e) => onChange(e.target.value)}>
        <MenuItem value="all">{allLabel}</MenuItem>
        {options.map((o) => <MenuItem key={getKey(o)} value={getKey(o)}>{getLabel(o)}</MenuItem>)}
      </Select>
    </FormControl>
  );

  const renderCertCell = (row) => (
    <Tooltip
      arrow
      placement="left"
      title={
        <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
          {[...row.certs].sort((a, b) => b.level - a.level).map((c) => (
            <div key={c.title} style={{ fontSize: 12, marginBottom: 4 }}>{c.title}</div>
          ))}
        </Box>
      }
    >
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
        {SECTION_ORDER.map((lvl, i) => (row.counts[i] > 0 ? (
          <Chip
            key={lvl}
            size="small"
            label={`${lvl.charAt(0)}${row.counts[i]}`}
            sx={{
              bgcolor: 'rgba(255,255,255,0.18)', color: 'inherit', fontWeight: 600,
              height: row.rank <= TOP_N ? 20 : 16, fontSize: row.rank <= TOP_N ? 13 : 11, borderRadius: '4px',
              '& .MuiChip-label': { px: 0.5 },
            }}
          />
        ) : null))}
        <Typography component="span" sx={{ fontWeight: 700, fontSize: 'inherit', ml: 0.5, minWidth: 18, textAlign: 'right' }}>
          {row.total}
        </Typography>
      </Box>
    </Tooltip>
  );

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Leaderboard</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        {scopeLabel}{!loading && ` — ${rankedCount} ${rankedCount === 1 ? 'person' : 'people'} ranked`}
      </Typography>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
        {renderFilter('Category', selectedCategory, setSelectedCategory, categoryOptions, 'All categories')}
        {renderFilter('Company', selectedCompany, setSelectedCompany, companies, 'All companies', (c) => c.id, (c) => c.name)}
        {renderFilter('Position', selectedPosition, setSelectedPosition, positionOptions, 'All positions')}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4, color: 'text.secondary' }}>
          <CircularProgress size={20} /> Loading leaderboard…
        </Box>
      ) : rankedCount === 0 ? (
        <Typography color="text.secondary" sx={{ py: 4 }}>
          No certificates match this selection yet.
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '75vh' }}>
          <Table stickyHeader size="small" sx={tableSx}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 28 }}>#</TableCell>
                <TableCell sx={{ width: 26 }} />
                <TableCell>Name</TableCell>
                <TableCell>Last name</TableCell>
                <TableCell sx={hideOnMobile}>Sector</TableCell>
                <TableCell sx={hideOnMobile}>Country</TableCell>
                <TableCell sx={hideOnMobile}>Company</TableCell>
                <TableCell align="right">Certificates</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {SECTION_ORDER.map((lvl) => {
                const rows = sections[lvl];
                if (!rows.length) return null;
                const showAll = expanded[lvl];
                const visible = showAll ? rows : rows.slice(0, SECTION_PAGE_SIZE);
                const colors = LEVEL_COLORS[lvl];

                return (
                  <React.Fragment key={lvl}>
                    <TableRow>
                      <TableCell
                        colSpan={COLS}
                        align="center"
                        sx={{
                          bgcolor: colors.header, color: '#fff', fontWeight: 700,
                          borderBottom: 'none', position: 'sticky', top: HEADER_H, zIndex: 1,
                        }}
                      >
                        {lvl}
                        <Typography component="span" sx={{ ml: 1, fontSize: 11, color: 'rgba(255,255,255,0.85)' }}>
                          {rows.length} {rows.length === 1 ? 'person' : 'people'}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {visible.map((r) => {
                      const isMe = currentUid && r.uid === currentUid;
                      const isTop = r.rank <= TOP_N; // top 3 of the current filter
                      return (
                        <TableRow
                          key={r.key}
                          sx={{
                            '& .MuiTableCell-root': {
                              bgcolor: colors.row,
                              color: isTop ? TOP_TEXT : '#fff',
                              borderBottom: '1px solid rgba(255,255,255,0.08)',
                              ...(isTop && {
                                py: '6px',
                                fontSize: { xs: 14, md: 16 },
                                fontWeight: 600,
                              }),
                            },
                            '&:hover .MuiTableCell-root': { filter: 'brightness(1.15)' },
                            // Your own row: gold accent bar on the left edge
                            ...(isMe && { '& .MuiTableCell-root:first-of-type': { boxShadow: 'inset 4px 0 0 #facc15' } }),
                          }}
                        >
                          <TableCell sx={{ fontWeight: 700 }}>
                            {r.rank}
                          </TableCell>
                          <TableCell>
                            <Avatar
                              src={r.avatar || undefined}
                              sx={isTop
                                ? { width: 32, height: 32, fontSize: 15, border: `2px solid ${TOP_TEXT}` }
                                : { width: 20, height: 20, fontSize: 11 }}
                            >
                              {(r.firstName || '?').charAt(0).toUpperCase()}
                            </Avatar>
                          </TableCell>
                          {textCell(isMe ? `${r.firstName || '—'} (you)` : r.firstName, { xs: 90, md: 140 })}
                          {textCell(r.lastName, { xs: 90, md: 140 })}
                          {textCell(r.sector, 160, hideOnMobile)}
                          {textCell(r.country, 120, hideOnMobile)}
                          {textCell(r.company, 180, hideOnMobile)}
                          <TableCell align="right">{renderCertCell(r)}</TableCell>
                        </TableRow>
                      );
                    })}

                    {rows.length > SECTION_PAGE_SIZE && (
                      <TableRow>
                        <TableCell
                          colSpan={COLS}
                          align="center"
                          sx={{ bgcolor: colors.row, borderBottom: '1px solid rgba(255,255,255,0.08)' }}
                        >
                          <Button
                            size="small"
                            sx={{ py: 0, minHeight: 0, fontSize: 12, textTransform: 'none', color: '#fff', textDecoration: 'underline' }}
                            onClick={() => setExpanded((e) => ({ ...e, [lvl]: !showAll }))}
                          >
                            {showAll ? `Show top ${SECTION_PAGE_SIZE}` : `Show all ${rows.length}`}
                          </Button>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

const mapStateToProps = (state) => ({
  userdata: state.userDialog.userDetails[0] || null,
  reAuthKey: state.reAuth?.reAuthKey || 0,
});

export default connect(mapStateToProps)(CertificateLeaderboard);
//////BACK END FIRST///////////////////////////////////////////////////////////////////
// import React, { useEffect, useMemo, useRef, useState } from 'react';
// import { connect } from 'react-redux';
// import {
//   Avatar, Box, Button, Chip, FormControl, InputLabel, MenuItem, Paper, Select,
//   Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
//   Typography, CircularProgress, Alert,
// } from '@mui/material';
// import firebase from '../store/firebase';

// /*
//  * Reads the precomputed leaderboard written by the m188-leaderboard Cloud Functions:
//  *   leaderboard/{companyID}/{userID} = { firstName, lastName, avatar, sector, country,
//  *                                        position, certs: [{ t, l, c }], total, topLevel }
//  * Certificate rules and thresholds live in the backend (modules/certificateEngine.js).
//  */

// const LEVELS = ['Foundational', 'Advanced', 'Distinguished', 'Elite', 'Pinnacle']; // index+1 = level
// const SECTION_ORDER = [...LEVELS].reverse(); // Pinnacle on top
// const SECTION_PAGE_SIZE = 25;

// // header = section divider row (grey, darkest at top)
// // row    = people rows in that section (blue, darkest at top)
// // Every shade keeps white text at WCAG AA contrast (≥ 4.5:1).
// const LEVEL_COLORS = {
//   Pinnacle:      { header: '#1f2328', row: '#0b1f4d' },
//   Elite:         { header: '#33383f', row: '#12347a' },
//   Distinguished: { header: '#454b54', row: '#1a4aa6' },
//   Advanced:      { header: '#565d67', row: '#2462c9' },
//   Foundational:  { header: '#666d77', row: '#3473dd' },
// };
// // Column-heading row: deep navy, darker than the Pinnacle divider beneath it
// const COLUMN_HEADER_BG = '#060f26';

// /* ------------------------------------------------------------------ */
// /* Pure helpers                                                        */
// /* ------------------------------------------------------------------ */

// // RTDB may return a stored array as an object; normalise to the shape the UI uses
// const toCerts = (stored) => Object.values(stored || {})
//   .filter((c) => c && c.t && c.l)
//   .map((c) => ({ title: c.t, level: Number(c.l), category: c.c || null }));

// // counts[0] = Pinnacle ... counts[4] = Foundational
// const countByLevel = (certs) => {
//   const counts = [0, 0, 0, 0, 0];
//   certs.forEach((c) => { counts[5 - c.level] += 1; });
//   return counts;
// };

// // Lexicographic: most Pinnacle wins, then Elite, ... then total, then name.
// const compareRows = (a, b) => {
//   for (let i = 0; i < 5; i++) {
//     if (b.counts[i] !== a.counts[i]) return b.counts[i] - a.counts[i];
//   }
//   if (b.total !== a.total) return b.total - a.total;
//   return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
// };

// /* ------------------------------------------------------------------ */
// /* Component                                                           */
// /* ------------------------------------------------------------------ */

// function CertificateLeaderboard({ userdata, reAuthKey }) {
//   const [companies, setCompanies] = useState([]);           // [{ id, name }]
//   const [selectedCompany, setSelectedCompany] = useState('all');
//   const [selectedCategory, setSelectedCategory] = useState('all');
//   const [selectedPosition, setSelectedPosition] = useState('all');
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [dataVersion, setDataVersion] = useState(0);         // bumps when cache fills
//   const [expanded, setExpanded] = useState({});              // { Pinnacle: true, ... }

//   // Loaded entries — filter changes never re-hit Firebase.
//   // all = { companyID: { userID: entry } }, companies[cid] = { userID: entry }
//   const cacheRef = useRef({ reAuthKey: null, all: null, companies: {} });

//   // Rows are keyed by userID (the userTestResults key), so match on that
//   const currentUid = userdata?.userID || null;

//   /* ---- Load companies -------------------------------------------- */
//   useEffect(() => {
//     if (!userdata) return;
//     firebase.database.ref('active-companies').once('value')
//       .then((snap) => {
//         const comps = [];
//         snap.forEach((child) => {
//           const val = child.val();
//           if (val?.companyID && val?.companyName) comps.push({ id: val.companyID, name: val.companyName });
//         });
//         comps.sort((a, b) => a.name.localeCompare(b.name));
//         setCompanies(comps);
//       })
//       .catch((err) => {
//         console.error('Error loading companies:', err);
//         setError('Could not load companies.');
//       });
//   }, [userdata, reAuthKey]);

//   /* ---- Load precomputed entries for the selected scope ------------ */
//   useEffect(() => {
//     if (!userdata || companies.length === 0) return undefined;
//     let cancelled = false;
//     const cache = cacheRef.current;

//     // Fresh auth → drop anything cached under the old session
//     if (cache.reAuthKey !== reAuthKey) {
//       cache.reAuthKey = reAuthKey;
//       cache.all = null;
//       cache.companies = {};
//     }

//     // "All companies" already loaded covers any single company too
//     const haveIt = selectedCompany === 'all'
//       ? !!cache.all
//       : !!(cache.all || cache.companies[selectedCompany]);
//     if (haveIt) {
//       setDataVersion((v) => v + 1);
//       setLoading(false);
//       return undefined;
//     }

//     const run = async () => {
//       setLoading(true);
//       setError(null);
//       try {
//         if (selectedCompany === 'all') {
//           const snap = await firebase.database.ref('leaderboard').once('value');
//           cache.all = snap.val() || {};
//         } else {
//           const snap = await firebase.database.ref(`leaderboard/${selectedCompany}`).once('value');
//           cache.companies[selectedCompany] = snap.val() || {};
//         }
//       } catch (err) {
//         console.error('Error loading leaderboard:', err);
//         if (!cancelled) setError('Could not load the leaderboard.');
//       }
//       if (!cancelled) {
//         setDataVersion((v) => v + 1);
//         setLoading(false);
//       }
//     };

//     run();
//     return () => { cancelled = true; };
//   }, [userdata, companies, selectedCompany, reAuthKey]);

//   /* ---- Base rows: everyone in the selected company scope ----------- */
//   const baseRows = useMemo(() => {
//     const cache = cacheRef.current;
//     const nameById = Object.fromEntries(companies.map((c) => [c.id, c.name]));

//     // { companyID: { userID: entry } } for the current scope
//     let scope = {};
//     if (selectedCompany === 'all') {
//       scope = cache.all || {};
//     } else {
//       const fromAll = cache.all && cache.all[selectedCompany];
//       scope = { [selectedCompany]: fromAll || cache.companies[selectedCompany] || {} };
//     }

//     const rows = [];
//     Object.entries(scope).forEach(([cid, users]) => {
//       if (!nameById[cid]) return; // only active companies, as before
//       Object.entries(users || {}).forEach(([userID, e]) => {
//         if (!e) return;
//         rows.push({
//           key: `${cid}:${userID}`,
//           uid: userID,
//           companyID: cid,
//           company: nameById[cid],
//           allCerts: toCerts(e.certs),
//           firstName: e.firstName || '',
//           lastName: e.lastName || '',
//           avatar: e.avatar || '',
//           sector: e.sector || '',
//           country: e.country || '',
//           position: e.position || '',
//         });
//       });
//     });
//     return rows;
//   }, [dataVersion, companies, selectedCompany]);

//   /* ---- Filter options (derived from the company scope) ------------ */
//   const categoryOptions = useMemo(() => {
//     const set = new Set();
//     baseRows.forEach((r) => r.allCerts.forEach((c) => { if (c.category) set.add(c.category); }));
//     return [...set].sort((a, b) => a.localeCompare(b));
//   }, [baseRows]);

//   const positionOptions = useMemo(() => {
//     const set = new Set();
//     baseRows.forEach((r) => { if (r.position) set.add(r.position); });
//     return [...set].sort((a, b) => a.localeCompare(b));
//   }, [baseRows]);

//   // Reset a filter if its value vanished after switching company
//   useEffect(() => {
//     if (selectedCategory !== 'all' && !categoryOptions.includes(selectedCategory)) setSelectedCategory('all');
//   }, [categoryOptions, selectedCategory]);
//   useEffect(() => {
//     if (selectedPosition !== 'all' && !positionOptions.includes(selectedPosition)) setSelectedPosition('all');
//   }, [positionOptions, selectedPosition]);

//   /* ---- Ranked + sectioned rows ------------------------------------ */
//   const { sections, rankedCount } = useMemo(() => {
//     const ranked = baseRows
//       .filter((r) => selectedPosition === 'all' || r.position === selectedPosition)
//       .map((r) => {
//         // Category filter: only certs tied to that category count.
//         // Cross-category "Leadership - X" certs have no single category, so they drop out.
//         const certs = selectedCategory === 'all'
//           ? r.allCerts
//           : r.allCerts.filter((c) => c.category === selectedCategory);
//         const counts = countByLevel(certs);
//         const highestIdx = counts.findIndex((n) => n > 0); // 0 = Pinnacle
//         return { ...r, certs, counts, total: certs.length, highest: SECTION_ORDER[highestIdx] };
//       })
//       .filter((r) => r.total > 0)
//       .sort(compareRows)
//       .map((r, i) => ({ ...r, rank: i + 1 }));

//     const grouped = Object.fromEntries(SECTION_ORDER.map((lvl) => [lvl, []]));
//     ranked.forEach((r) => grouped[r.highest].push(r));
//     return { sections: grouped, rankedCount: ranked.length };
//   }, [baseRows, selectedCategory, selectedPosition]);

//   /* ---- Render ------------------------------------------------------ */
//   if (!userdata) return <Box sx={{ p: 2 }}>Please sign in.</Box>;

//   const scopeLabel = [
//     selectedCategory !== 'all' ? selectedCategory : 'All categories',
//     selectedCompany !== 'all' ? companies.find((c) => c.id === selectedCompany)?.name : 'All companies',
//     selectedPosition !== 'all' ? selectedPosition : null,
//   ].filter(Boolean).join(' · ');

//   const hideOnMobile = { display: { xs: 'none', md: 'table-cell' } };
//   const COLS = 8;

//   // Compact, single-line rows everywhere (mobile + desktop)
//   const HEADER_H = 26; // px — sticky section headers sit right under the column header
//   const tableSx = {
//     '& .MuiTableCell-root': {
//       whiteSpace: 'nowrap',
//       py: '2px',
//       px: { xs: 0.75, md: 1 },
//       fontSize: { xs: 12, md: 13 },
//       lineHeight: 1.25,
//     },
//     '& .MuiTableHead-root .MuiTableCell-root': {
//       height: HEADER_H, fontWeight: 700, color: '#fff',
//       bgcolor: COLUMN_HEADER_BG, borderBottom: 'none',
//     },
//   };
//   // Long text: one line, ellipsis, full value on hover
//   const truncate = (maxWidth) => ({ maxWidth, overflow: 'hidden', textOverflow: 'ellipsis' });
//   const textCell = (value, maxWidth, extraSx) => (
//     <TableCell sx={{ ...truncate(maxWidth), ...extraSx }} title={value || ''}>
//       {value || '—'}
//     </TableCell>
//   );

//   const renderFilter = (label, value, onChange, options, allLabel, getKey = (o) => o, getLabel = (o) => o) => (
//     <FormControl size="small" sx={{ minWidth: 200, flex: '1 1 200px' }} disabled={loading}>
//       <InputLabel>{label}</InputLabel>
//       <Select label={label} value={value} onChange={(e) => onChange(e.target.value)}>
//         <MenuItem value="all">{allLabel}</MenuItem>
//         {options.map((o) => <MenuItem key={getKey(o)} value={getKey(o)}>{getLabel(o)}</MenuItem>)}
//       </Select>
//     </FormControl>
//   );

//   const renderCertCell = (row) => (
//     <Tooltip
//       arrow
//       placement="left"
//       title={
//         <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
//           {[...row.certs].sort((a, b) => b.level - a.level).map((c) => (
//             <div key={c.title} style={{ fontSize: 12, marginBottom: 4 }}>{c.title}</div>
//           ))}
//         </Box>
//       }
//     >
//       <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
//         {SECTION_ORDER.map((lvl, i) => (row.counts[i] > 0 ? (
//           <Chip
//             key={lvl}
//             size="small"
//             label={`${lvl.charAt(0)}${row.counts[i]}`}
//             sx={{
//               bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 600,
//               height: 16, fontSize: 11, borderRadius: '4px',
//               '& .MuiChip-label': { px: 0.5 },
//             }}
//           />
//         ) : null))}
//         <Typography component="span" sx={{ fontWeight: 700, fontSize: 'inherit', ml: 0.5, minWidth: 18, textAlign: 'right' }}>
//           {row.total}
//         </Typography>
//       </Box>
//     </Tooltip>
//   );

//   return (
//     <Box sx={{ p: { xs: 1.5, md: 2.5 }, maxWidth: 1200, mx: 'auto' }}>
//       <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Certificate Leaderboard</Typography>
//       <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
//         {scopeLabel}{!loading && ` — ${rankedCount} ${rankedCount === 1 ? 'person' : 'people'} ranked`}
//       </Typography>

//       <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
//         {renderFilter('Category', selectedCategory, setSelectedCategory, categoryOptions, 'All categories')}
//         {renderFilter('Company', selectedCompany, setSelectedCompany, companies, 'All companies', (c) => c.id, (c) => c.name)}
//         {renderFilter('Position', selectedPosition, setSelectedPosition, positionOptions, 'All positions')}
//       </Box>

//       {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

//       {loading ? (
//         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4, color: 'text.secondary' }}>
//           <CircularProgress size={20} /> Loading leaderboard…
//         </Box>
//       ) : rankedCount === 0 ? (
//         <Typography color="text.secondary" sx={{ py: 4 }}>
//           No certificates match this selection yet.
//         </Typography>
//       ) : (
//         <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '75vh' }}>
//           <Table stickyHeader size="small" sx={tableSx}>
//             <TableHead>
//               <TableRow>
//                 <TableCell sx={{ width: 28 }}>#</TableCell>
//                 <TableCell sx={{ width: 26 }} />
//                 <TableCell>Name</TableCell>
//                 <TableCell>Last name</TableCell>
//                 <TableCell sx={hideOnMobile}>Sector</TableCell>
//                 <TableCell sx={hideOnMobile}>Country</TableCell>
//                 <TableCell sx={hideOnMobile}>Company</TableCell>
//                 <TableCell align="right">Certificates</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {SECTION_ORDER.map((lvl) => {
//                 const rows = sections[lvl];
//                 if (!rows.length) return null;
//                 const showAll = expanded[lvl];
//                 const visible = showAll ? rows : rows.slice(0, SECTION_PAGE_SIZE);
//                 const colors = LEVEL_COLORS[lvl];

//                 return (
//                   <React.Fragment key={lvl}>
//                     <TableRow>
//                       <TableCell
//                         colSpan={COLS}
//                         align="center"
//                         sx={{
//                           bgcolor: colors.header, color: '#fff', fontWeight: 700,
//                           borderBottom: 'none', position: 'sticky', top: HEADER_H, zIndex: 1,
//                         }}
//                       >
//                         {lvl}
//                         <Typography component="span" sx={{ ml: 1, fontSize: 11, color: 'rgba(255,255,255,0.85)' }}>
//                           {rows.length} {rows.length === 1 ? 'person' : 'people'}
//                         </Typography>
//                       </TableCell>
//                     </TableRow>

//                     {visible.map((r) => {
//                       const isMe = currentUid && r.uid === currentUid;
//                       return (
//                         <TableRow
//                           key={r.key}
//                           sx={{
//                             '& .MuiTableCell-root': {
//                               bgcolor: colors.row,
//                               color: '#fff',
//                               borderBottom: '1px solid rgba(255,255,255,0.08)',
//                             },
//                             '&:hover .MuiTableCell-root': { filter: 'brightness(1.15)' },
//                             // Your own row: gold accent bar on the left edge
//                             ...(isMe && { '& .MuiTableCell-root:first-of-type': { boxShadow: 'inset 4px 0 0 #facc15' } }),
//                           }}
//                         >
//                           <TableCell sx={{ fontWeight: 700 }}>
//                             {r.rank}
//                           </TableCell>
//                           <TableCell>
//                             <Avatar src={r.avatar || undefined} sx={{ width: 20, height: 20, fontSize: 11 }}>
//                               {(r.firstName || '?').charAt(0).toUpperCase()}
//                             </Avatar>
//                           </TableCell>
//                           {textCell(isMe ? `${r.firstName || '—'} (you)` : r.firstName, { xs: 90, md: 140 })}
//                           {textCell(r.lastName, { xs: 90, md: 140 })}
//                           {textCell(r.sector, 160, hideOnMobile)}
//                           {textCell(r.country, 120, hideOnMobile)}
//                           {textCell(r.company, 180, hideOnMobile)}
//                           <TableCell align="right">{renderCertCell(r)}</TableCell>
//                         </TableRow>
//                       );
//                     })}

//                     {rows.length > SECTION_PAGE_SIZE && (
//                       <TableRow>
//                         <TableCell
//                           colSpan={COLS}
//                           align="center"
//                           sx={{ bgcolor: colors.row, borderBottom: '1px solid rgba(255,255,255,0.08)' }}
//                         >
//                           <Button
//                             size="small"
//                             sx={{ py: 0, minHeight: 0, fontSize: 12, textTransform: 'none', color: '#fff', textDecoration: 'underline' }}
//                             onClick={() => setExpanded((e) => ({ ...e, [lvl]: !showAll }))}
//                           >
//                             {showAll ? `Show top ${SECTION_PAGE_SIZE}` : `Show all ${rows.length}`}
//                           </Button>
//                         </TableCell>
//                       </TableRow>
//                     )}
//                   </React.Fragment>
//                 );
//               })}
//             </TableBody>
//           </Table>
//         </TableContainer>
//       )}
//     </Box>
//   );
// }

// const mapStateToProps = (state) => ({
//   userdata: state.userDialog.userDetails[0] || null,
//   reAuthKey: state.reAuth?.reAuthKey || 0,
// });

// export default connect(mapStateToProps)(CertificateLeaderboard);

/////FRONT END ONLY LAST VERSION//////////////////////////////////////////////////////////////////////////////////
// import React, { useEffect, useMemo, useRef, useState } from 'react';
// import { connect } from 'react-redux';
// import {
//   Avatar, Box, Button, Chip, FormControl, InputLabel, MenuItem, Paper, Select,
//   Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
//   Typography, CircularProgress, Alert,
// } from '@mui/material';
// import firebase from '../store/firebase';
// import { calculateTestPassed, calculateLCEarned } from '../utils/test_utils';

// /* ------------------------------------------------------------------ */
// /* Config                                                              */
// /* ------------------------------------------------------------------ */

// // Index + 1 === level number used by computeLevel()
// const LEVELS = ['Foundational', 'Advanced', 'Distinguished', 'Elite', 'Pinnacle'];
// const SECTION_ORDER = [...LEVELS].reverse(); // Pinnacle on top

// // One threshold set for everyone — a leaderboard must rank all users on the
// // same scale regardless of who is viewing it.
// const LEVEL_THRESHOLDS = [5, 10, 18, 30, 45];
// const MIN_TESTS_FOR_CERT = 1;

// const SECTION_PAGE_SIZE = 25;
// const PROFILE_FETCH_BATCH = 50;

// // userTestResults/{companyID}/{userID}/{thoughtID} is keyed by userID (not auth uid),
// // and users/{userID} holds the same profile data as user/{uid} — so read it directly.
// // Field keys: the first non-empty key in each list wins.
// const PROFILE_PATH = (userID) => `users/${userID}`;
// const PROFILE_FIELDS = {
//   firstName: ['firstName', 'firstname', 'name'],
//   lastName:  ['lastName', 'lastname', 'surname'],
//   avatar:    ['photoURL', 'avatar', 'profilePic', 'photo'],
//   sector:    ['sector', 'industry'],
//   country:   ['nationality', 'country'],
//   position:  ['position', 'jobTitle', 'title'],
// };

// // header = section divider row (grey, darkest at top)
// // row    = people rows in that section (blue, darkest at top)
// // Every shade keeps white text at WCAG AA contrast (≥ 4.5:1).
// const LEVEL_COLORS = {
//   Pinnacle:      { header: '#1f2328', row: '#0b1f4d' },
//   Elite:         { header: '#33383f', row: '#12347a' },
//   Distinguished: { header: '#454b54', row: '#1a4aa6' },
//   Advanced:      { header: '#565d67', row: '#2462c9' },
//   Foundational:  { header: '#666d77', row: '#3473dd' },
// };
// // Column-heading row: deep navy, darker than the Pinnacle divider beneath it
// const COLUMN_HEADER_BG = '#060f26';

// /* ------------------------------------------------------------------ */
// /* Pure helpers                                                        */
// /* ------------------------------------------------------------------ */

// const cleanMainCategory = (name) => {
//   if (!name) return '';
//   return String(name).split(/\s*[–—-]\s*/)[0].trim();
// };

// const computeLevel = (lc) => {
//   if (!lc || lc <= 0) return 0;
//   let lvl = 0;
//   LEVEL_THRESHOLDS.forEach((t, i) => { if (lc >= t) lvl = i + 1; });
//   return lvl;
// };

// const pick = (obj, keys) => {
//   if (!obj) return '';
//   for (const k of keys) {
//     const v = obj[k];
//     if (v !== undefined && v !== null && String(v).trim() !== '') return String(v).trim();
//   }
//   return '';
// };

// let warnedUnmappedProfile = false;
// const normalizeProfile = (raw) => {
//   const p = {};
//   Object.entries(PROFILE_FIELDS).forEach(([field, keys]) => { p[field] = pick(raw, keys); });

//   // Dev aid: if a real record maps to nothing, show its keys once so PROFILE_FIELDS can be fixed
//   if (process.env.NODE_ENV !== 'production' && raw && !warnedUnmappedProfile
//       && Object.values(p).every((v) => !v)) {
//     warnedUnmappedProfile = true;
//     console.warn('[CertificateLeaderboard] Profile fields not matched. Record keys:', Object.keys(raw));
//   }
//   return p;
// };

// /**
//  * Same certificate rules as CompanyCertificates, but returns structured certs
//  * so we can filter by category and count by level.
//  * Each cert: { title, level (1-5), levelName, type, category|null, sub|null }
//  *   type 'catSub'   → category + subcategory
//  *   type 'crossSub' → subcategory across >1 category ("Leadership - X"), category = null
//  *   type 'catTotal' → whole category across >1 subcategory
//  */
// const computeUserCerts = (userTests) => {
//   const byCatSub = {};
//   const bySubAll = {};
//   const byCategoryTotals = {};

//   Object.values(userTests).forEach((test) => {
//     if (!test || typeof test !== 'object') return;
//     const total = parseInt(test.totalAnswered || test.qlen || 0, 10);
//     const score = parseInt(test.testscore || 0, 10);
//     const difficulty = test.difficulty || 'basic';
//     const passed = typeof test.testPassed === 'boolean'
//       ? test.testPassed
//       : calculateTestPassed(score, total, difficulty);
//     if (!passed) return;

//     const lc = Number(typeof test.lcEarned === 'number'
//       ? test.lcEarned
//       : calculateLCEarned(score, true, difficulty)) || 0;
//     const cat = test.leadCategory || test.categoryType || 'general';
//     const sub = test.leadSubCategory || '';
//     if (!sub) return;

//     byCatSub[cat] = byCatSub[cat] || {};
//     byCatSub[cat][sub] = byCatSub[cat][sub] || { lc: 0, tests: 0 };
//     byCatSub[cat][sub].lc += lc;
//     byCatSub[cat][sub].tests += 1;

//     bySubAll[sub] = bySubAll[sub] || { lc: 0, tests: 0, categories: new Set() };
//     bySubAll[sub].lc += lc;
//     bySubAll[sub].tests += 1;
//     bySubAll[sub].categories.add(cat);

//     byCategoryTotals[cat] = byCategoryTotals[cat] || { lc: 0, tests: 0, subs: new Set() };
//     byCategoryTotals[cat].lc += lc;
//     byCategoryTotals[cat].tests += 1;
//     byCategoryTotals[cat].subs.add(sub);
//   });

//   const certs = new Map(); // title -> cert (dedupe, mirrors the old Set)
//   const add = (lvl, type, category, sub, title) => {
//     if (lvl > 0 && !certs.has(title)) {
//       certs.set(title, { title, level: lvl, levelName: LEVELS[lvl - 1], type, category, sub });
//     }
//   };

//   Object.entries(byCatSub).forEach(([cat, subs]) => {
//     const clean = cleanMainCategory(cat);
//     Object.entries(subs).forEach(([sub, info]) => {
//       if (info.tests < MIN_TESTS_FOR_CERT) return;
//       const lvl = computeLevel(info.lc);
//       add(lvl, 'catSub', clean, sub,
//         `Real-Time ${LEVELS[lvl - 1]} Certificate In ${clean} ${sub}`);
//     });
//   });

//   Object.entries(bySubAll).forEach(([sub, info]) => {
//     if (info.categories.size <= 1 || info.tests < MIN_TESTS_FOR_CERT) return;
//     const lvl = computeLevel(info.lc);
//     add(lvl, 'crossSub', null, sub,
//       `Real-Time ${LEVELS[lvl - 1]} Certificate In Leadership - ${sub}`);
//   });

//   Object.entries(byCategoryTotals).forEach(([cat, info]) => {
//     if (info.subs.size <= 1 || info.tests < MIN_TESTS_FOR_CERT) return;
//     const clean = cleanMainCategory(cat);
//     const lvl = computeLevel(info.lc);
//     add(lvl, 'catTotal', clean, null,
//       `Real-Time ${LEVELS[lvl - 1]} Certificate In ${clean} Leadership`);
//   });

//   return [...certs.values()];
// };

// // counts[0] = Pinnacle ... counts[4] = Foundational
// const countByLevel = (certs) => {
//   const counts = [0, 0, 0, 0, 0];
//   certs.forEach((c) => { counts[5 - c.level] += 1; });
//   return counts;
// };

// // Lexicographic: most Pinnacle wins, then Elite, ... then total, then name.
// const compareRows = (a, b) => {
//   for (let i = 0; i < 5; i++) {
//     if (b.counts[i] !== a.counts[i]) return b.counts[i] - a.counts[i];
//   }
//   if (b.total !== a.total) return b.total - a.total;
//   return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
// };

// /* ------------------------------------------------------------------ */
// /* Component                                                           */
// /* ------------------------------------------------------------------ */

// function CertificateLeaderboard({ userdata, reAuthKey }) {
//   const [companies, setCompanies] = useState([]);           // [{ id, name }]
//   const [selectedCompany, setSelectedCompany] = useState('all');
//   const [selectedCategory, setSelectedCategory] = useState('all');
//   const [selectedPosition, setSelectedPosition] = useState('all');
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [dataVersion, setDataVersion] = useState(0);         // bumps when cache fills
//   const [expanded, setExpanded] = useState({});              // { Pinnacle: true, ... }

//   // Raw data cache — filter changes never re-hit Firebase.
//   // companies[cid] = { [uid]: certs[] }, profiles[uid] = normalized profile
//   const cacheRef = useRef({ reAuthKey: null, companies: {}, profiles: {} });

//   // Rows are keyed by userID (the userTestResults key), so match on that
//   const currentUid = userdata?.userID || null;

//   /* ---- Load companies -------------------------------------------- */
//   useEffect(() => {
//     if (!userdata) return;
//     firebase.database.ref('active-companies').once('value')
//       .then((snap) => {
//         const comps = [];
//         snap.forEach((child) => {
//           const val = child.val();
//           if (val?.companyID && val?.companyName) comps.push({ id: val.companyID, name: val.companyName });
//         });
//         comps.sort((a, b) => a.name.localeCompare(b.name));
//         setCompanies(comps);
//       })
//       .catch((err) => {
//         console.error('Error loading companies:', err);
//         setError('Could not load companies.');
//       });
//   }, [userdata, reAuthKey]);

//   /* ---- Load test results + profiles for the selected scope -------- */
//   useEffect(() => {
//     if (!userdata || companies.length === 0) return undefined;
//     let cancelled = false;
//     const cache = cacheRef.current;

//     // Fresh auth → drop anything cached under the old session
//     if (cache.reAuthKey !== reAuthKey) {
//       cache.reAuthKey = reAuthKey;
//       cache.companies = {};
//       cache.profiles = {};
//     }

//     const run = async () => {
//       setLoading(true);
//       setError(null);

//       const targetIds = selectedCompany === 'all' ? companies.map((c) => c.id) : [selectedCompany];
//       const missingCompanies = targetIds.filter((cid) => !cache.companies[cid]);

//       await Promise.all(missingCompanies.map(async (cid) => {
//         try {
//           const snap = await firebase.database.ref(`userTestResults/${cid}`).once('value');
//           const users = {};
//           Object.entries(snap.val() || {}).forEach(([uid, tests]) => {
//             if (!tests || typeof tests !== 'object') return;
//             const certs = computeUserCerts(tests);
//             if (certs.length) users[uid] = certs;
//           });
//           cache.companies[cid] = users;
//         } catch (err) {
//           console.error(`Error reading company ${cid}:`, err);
//           cache.companies[cid] = {};
//         }
//       }));

//       // Profiles only for users who actually hold certificates
//       const uids = new Set();
//       targetIds.forEach((cid) => Object.keys(cache.companies[cid] || {}).forEach((u) => uids.add(u)));
//       const missingProfiles = [...uids].filter((u) => !(u in cache.profiles));

//       for (let i = 0; i < missingProfiles.length; i += PROFILE_FETCH_BATCH) {
//         if (cancelled) return;
//         const batch = missingProfiles.slice(i, i + PROFILE_FETCH_BATCH);
//         // eslint-disable-next-line no-await-in-loop
//         await Promise.all(batch.map(async (uid) => {
//           try {
//             const snap = await firebase.database.ref(PROFILE_PATH(uid)).once('value');
//             cache.profiles[uid] = normalizeProfile(snap.val());
//           } catch (err) {
//             cache.profiles[uid] = normalizeProfile(null);
//           }
//         }));
//       }

//       if (!cancelled) {
//         setDataVersion((v) => v + 1);
//         setLoading(false);
//       }
//     };

//     run();
//     return () => { cancelled = true; };
//   }, [userdata, companies, selectedCompany, reAuthKey]);

//   /* ---- Base rows: everyone in the selected company scope ----------- */
//   const baseRows = useMemo(() => {
//     const cache = cacheRef.current;
//     const nameById = Object.fromEntries(companies.map((c) => [c.id, c.name]));
//     const targetIds = selectedCompany === 'all' ? companies.map((c) => c.id) : [selectedCompany];
//     const rows = [];
//     targetIds.forEach((cid) => {
//       Object.entries(cache.companies[cid] || {}).forEach(([uid, certs]) => {
//         const profile = cache.profiles[uid] || normalizeProfile(null);
//         rows.push({
//           key: `${cid}:${uid}`,
//           uid,
//           companyID: cid,
//           company: nameById[cid] || '',
//           allCerts: certs,
//           ...profile,
//         });
//       });
//     });
//     return rows;
//   }, [dataVersion, companies, selectedCompany]);

//   /* ---- Filter options (derived from the company scope) ------------ */
//   const categoryOptions = useMemo(() => {
//     const set = new Set();
//     baseRows.forEach((r) => r.allCerts.forEach((c) => { if (c.category) set.add(c.category); }));
//     return [...set].sort((a, b) => a.localeCompare(b));
//   }, [baseRows]);

//   const positionOptions = useMemo(() => {
//     const set = new Set();
//     baseRows.forEach((r) => { if (r.position) set.add(r.position); });
//     return [...set].sort((a, b) => a.localeCompare(b));
//   }, [baseRows]);

//   // Reset a filter if its value vanished after switching company
//   useEffect(() => {
//     if (selectedCategory !== 'all' && !categoryOptions.includes(selectedCategory)) setSelectedCategory('all');
//   }, [categoryOptions, selectedCategory]);
//   useEffect(() => {
//     if (selectedPosition !== 'all' && !positionOptions.includes(selectedPosition)) setSelectedPosition('all');
//   }, [positionOptions, selectedPosition]);

//   /* ---- Ranked + sectioned rows ------------------------------------ */
//   const { sections, rankedCount } = useMemo(() => {
//     const ranked = baseRows
//       .filter((r) => selectedPosition === 'all' || r.position === selectedPosition)
//       .map((r) => {
//         // Category filter: only certs tied to that category count.
//         // Cross-category "Leadership - X" certs have no single category, so they drop out.
//         const certs = selectedCategory === 'all'
//           ? r.allCerts
//           : r.allCerts.filter((c) => c.category === selectedCategory);
//         const counts = countByLevel(certs);
//         const highestIdx = counts.findIndex((n) => n > 0); // 0 = Pinnacle
//         return { ...r, certs, counts, total: certs.length, highest: SECTION_ORDER[highestIdx] };
//       })
//       .filter((r) => r.total > 0)
//       .sort(compareRows)
//       .map((r, i) => ({ ...r, rank: i + 1 }));

//     const grouped = Object.fromEntries(SECTION_ORDER.map((lvl) => [lvl, []]));
//     ranked.forEach((r) => grouped[r.highest].push(r));
//     return { sections: grouped, rankedCount: ranked.length };
//   }, [baseRows, selectedCategory, selectedPosition]);

//   /* ---- Render ------------------------------------------------------ */
//   if (!userdata) return <Box sx={{ p: 2 }}>Please sign in.</Box>;

//   const scopeLabel = [
//     selectedCategory !== 'all' ? selectedCategory : 'All categories',
//     selectedCompany !== 'all' ? companies.find((c) => c.id === selectedCompany)?.name : 'All companies',
//     selectedPosition !== 'all' ? selectedPosition : null,
//   ].filter(Boolean).join(' · ');

//   const hideOnMobile = { display: { xs: 'none', md: 'table-cell' } };
//   const COLS = 8;

//   // Compact, single-line rows everywhere (mobile + desktop)
//   const HEADER_H = 26; // px — sticky section headers sit right under the column header
//   const tableSx = {
//     '& .MuiTableCell-root': {
//       whiteSpace: 'nowrap',
//       py: '2px',
//       px: { xs: 0.75, md: 1 },
//       fontSize: { xs: 12, md: 13 },
//       lineHeight: 1.25,
//     },
//     '& .MuiTableHead-root .MuiTableCell-root': {
//       height: HEADER_H, fontWeight: 700, color: '#fff',
//       bgcolor: COLUMN_HEADER_BG, borderBottom: 'none',
//     },
//   };
//   // Long text: one line, ellipsis, full value on hover
//   const truncate = (maxWidth) => ({ maxWidth, overflow: 'hidden', textOverflow: 'ellipsis' });
//   const textCell = (value, maxWidth, extraSx) => (
//     <TableCell sx={{ ...truncate(maxWidth), ...extraSx }} title={value || ''}>
//       {value || '—'}
//     </TableCell>
//   );

//   const renderFilter = (label, value, onChange, options, allLabel, getKey = (o) => o, getLabel = (o) => o) => (
//     <FormControl size="small" sx={{ minWidth: 200, flex: '1 1 200px' }} disabled={loading}>
//       <InputLabel>{label}</InputLabel>
//       <Select label={label} value={value} onChange={(e) => onChange(e.target.value)}>
//         <MenuItem value="all">{allLabel}</MenuItem>
//         {options.map((o) => <MenuItem key={getKey(o)} value={getKey(o)}>{getLabel(o)}</MenuItem>)}
//       </Select>
//     </FormControl>
//   );

//   const renderCertCell = (row) => (
//     <Tooltip
//       arrow
//       placement="left"
//       title={
//         <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
//           {[...row.certs].sort((a, b) => b.level - a.level).map((c) => (
//             <div key={c.title} style={{ fontSize: 12, marginBottom: 4 }}>{c.title}</div>
//           ))}
//         </Box>
//       }
//     >
//       <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
//         {SECTION_ORDER.map((lvl, i) => (row.counts[i] > 0 ? (
//           <Chip
//             key={lvl}
//             size="small"
//             label={`${lvl.charAt(0)}${row.counts[i]}`}
//             sx={{
//               bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 600,
//               height: 16, fontSize: 11, borderRadius: '4px',
//               '& .MuiChip-label': { px: 0.5 },
//             }}
//           />
//         ) : null))}
//         <Typography component="span" sx={{ fontWeight: 700, fontSize: 'inherit', ml: 0.5, minWidth: 18, textAlign: 'right' }}>
//           {row.total}
//         </Typography>
//       </Box>
//     </Tooltip>
//   );

//   return (
//     <Box sx={{ p: { xs: 1.5, md: 2.5 }, maxWidth: 1200, mx: 'auto' }}>
//       <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Certificate Leaderboard</Typography>
//       <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
//         {scopeLabel}{!loading && ` — ${rankedCount} ${rankedCount === 1 ? 'person' : 'people'} ranked`}
//       </Typography>

//       <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
//         {renderFilter('Category', selectedCategory, setSelectedCategory, categoryOptions, 'All categories')}
//         {renderFilter('Company', selectedCompany, setSelectedCompany, companies, 'All companies', (c) => c.id, (c) => c.name)}
//         {renderFilter('Position', selectedPosition, setSelectedPosition, positionOptions, 'All positions')}
//       </Box>

//       {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

//       {loading ? (
//         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4, color: 'text.secondary' }}>
//           <CircularProgress size={20} /> Loading leaderboard…
//         </Box>
//       ) : rankedCount === 0 ? (
//         <Typography color="text.secondary" sx={{ py: 4 }}>
//           No certificates match this selection yet.
//         </Typography>
//       ) : (
//         <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '75vh' }}>
//           <Table stickyHeader size="small" sx={tableSx}>
//             <TableHead>
//               <TableRow>
//                 <TableCell sx={{ width: 28 }}>#</TableCell>
//                 <TableCell sx={{ width: 26 }} />
//                 <TableCell>Name</TableCell>
//                 <TableCell>Last name</TableCell>
//                 <TableCell sx={hideOnMobile}>Sector</TableCell>
//                 <TableCell sx={hideOnMobile}>Country</TableCell>
//                 <TableCell sx={hideOnMobile}>Company</TableCell>
//                 <TableCell align="right">Certificates</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {SECTION_ORDER.map((lvl) => {
//                 const rows = sections[lvl];
//                 if (!rows.length) return null;
//                 const showAll = expanded[lvl];
//                 const visible = showAll ? rows : rows.slice(0, SECTION_PAGE_SIZE);
//                 const colors = LEVEL_COLORS[lvl];

//                 return (
//                   <React.Fragment key={lvl}>
//                     <TableRow>
//                       <TableCell
//                         colSpan={COLS}
//                         align="center"
//                         sx={{
//                           bgcolor: colors.header, color: '#fff', fontWeight: 700,
//                           borderBottom: 'none', position: 'sticky', top: HEADER_H, zIndex: 1,
//                         }}
//                       >
//                         {lvl}
//                         <Typography component="span" sx={{ ml: 1, fontSize: 11, color: 'rgba(255,255,255,0.85)' }}>
//                           {rows.length} {rows.length === 1 ? 'person' : 'people'}
//                         </Typography>
//                       </TableCell>
//                     </TableRow>

//                     {visible.map((r) => {
//                       const isMe = currentUid && r.uid === currentUid;
//                       return (
//                         <TableRow
//                           key={r.key}
//                           sx={{
//                             '& .MuiTableCell-root': {
//                               bgcolor: colors.row,
//                               color: '#fff',
//                               borderBottom: '1px solid rgba(255,255,255,0.08)',
//                             },
//                             '&:hover .MuiTableCell-root': { filter: 'brightness(1.15)' },
//                             // Your own row: gold accent bar on the left edge
//                             ...(isMe && { '& .MuiTableCell-root:first-of-type': { boxShadow: 'inset 4px 0 0 #facc15' } }),
//                           }}
//                         >
//                           <TableCell sx={{ fontWeight: 700 }}>
//                             {r.rank}
//                           </TableCell>
//                           <TableCell>
//                             <Avatar src={r.avatar || undefined} sx={{ width: 20, height: 20, fontSize: 11 }}>
//                               {(r.firstName || '?').charAt(0).toUpperCase()}
//                             </Avatar>
//                           </TableCell>
//                           {textCell(isMe ? `${r.firstName || '—'} (you)` : r.firstName, { xs: 90, md: 140 })}
//                           {textCell(r.lastName, { xs: 90, md: 140 })}
//                           {textCell(r.sector, 160, hideOnMobile)}
//                           {textCell(r.country, 120, hideOnMobile)}
//                           {textCell(r.company, 180, hideOnMobile)}
//                           <TableCell align="right">{renderCertCell(r)}</TableCell>
//                         </TableRow>
//                       );
//                     })}

//                     {rows.length > SECTION_PAGE_SIZE && (
//                       <TableRow>
//                         <TableCell
//                           colSpan={COLS}
//                           align="center"
//                           sx={{ bgcolor: colors.row, borderBottom: '1px solid rgba(255,255,255,0.08)' }}
//                         >
//                           <Button
//                             size="small"
//                             sx={{ py: 0, minHeight: 0, fontSize: 12, textTransform: 'none', color: '#fff', textDecoration: 'underline' }}
//                             onClick={() => setExpanded((e) => ({ ...e, [lvl]: !showAll }))}
//                           >
//                             {showAll ? `Show top ${SECTION_PAGE_SIZE}` : `Show all ${rows.length}`}
//                           </Button>
//                         </TableCell>
//                       </TableRow>
//                     )}
//                   </React.Fragment>
//                 );
//               })}
//             </TableBody>
//           </Table>
//         </TableContainer>
//       )}
//     </Box>
//   );
// }

// const mapStateToProps = (state) => ({
//   userdata: state.userDialog.userDetails[0] || null,
//   reAuthKey: state.reAuth?.reAuthKey || 0,
// });

// export default connect(mapStateToProps)(CertificateLeaderboard);
///////////////////////////////////////////////////////////////////////////////////////
// import React, { useEffect, useMemo, useRef, useState } from 'react';
// import { connect } from 'react-redux';
// import {
//   Avatar, Box, Button, Chip, FormControl, InputLabel, MenuItem, Paper, Select,
//   Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
//   Typography, CircularProgress, Alert,
// } from '@mui/material';
// import firebase from '../store/firebase';
// import { calculateTestPassed, calculateLCEarned } from '../utils/test_utils';

// /* ------------------------------------------------------------------ */
// /* Config                                                              */
// /* ------------------------------------------------------------------ */

// // Index + 1 === level number used by computeLevel()
// const LEVELS = ['Foundational', 'Advanced', 'Distinguished', 'Elite', 'Pinnacle'];
// const SECTION_ORDER = [...LEVELS].reverse(); // Pinnacle on top

// // One threshold set for everyone — a leaderboard must rank all users on the
// // same scale regardless of who is viewing it.
// const LEVEL_THRESHOLDS = [5, 10, 18, 30, 45];
// const MIN_TESTS_FOR_CERT = 1;

// const SECTION_PAGE_SIZE = 25;
// const PROFILE_FETCH_BATCH = 50;

// // userTestResults/{companyID}/{userID}/{thoughtID} is keyed by userID (not auth uid),
// // and users/{userID} holds the same profile data as user/{uid} — so read it directly.
// // Field keys: the first non-empty key in each list wins.
// const PROFILE_PATH = (userID) => `users/${userID}`;
// const PROFILE_FIELDS = {
//   firstName: ['firstName', 'firstname', 'name'],
//   lastName:  ['lastName', 'lastname', 'surname'],
//   avatar:    ['photoURL', 'avatar', 'profilePic', 'photo'],
//   sector:    ['sector', 'industry'],
//   country:   ['country'],
//   position:  ['position', 'jobTitle', 'title'],
// };

// const LEVEL_COLORS = {
//   Pinnacle:      { main: '#7c3aed', bg: '#f5f3ff' },
//   Elite:         { main: '#b45309', bg: '#fffbeb' },
//   Distinguished: { main: '#0f766e', bg: '#f0fdfa' },
//   Advanced:      { main: '#1d4ed8', bg: '#eff6ff' },
//   Foundational:  { main: '#4b5563', bg: '#f9fafb' },
// };

// /* ------------------------------------------------------------------ */
// /* Pure helpers                                                        */
// /* ------------------------------------------------------------------ */

// const cleanMainCategory = (name) => {
//   if (!name) return '';
//   return String(name).split(/\s*[–—-]\s*/)[0].trim();
// };

// const computeLevel = (lc) => {
//   if (!lc || lc <= 0) return 0;
//   let lvl = 0;
//   LEVEL_THRESHOLDS.forEach((t, i) => { if (lc >= t) lvl = i + 1; });
//   return lvl;
// };

// const pick = (obj, keys) => {
//   if (!obj) return '';
//   for (const k of keys) {
//     const v = obj[k];
//     if (v !== undefined && v !== null && String(v).trim() !== '') return String(v).trim();
//   }
//   return '';
// };

// let warnedUnmappedProfile = false;
// const normalizeProfile = (raw) => {
//   const p = {};
//   Object.entries(PROFILE_FIELDS).forEach(([field, keys]) => { p[field] = pick(raw, keys); });

//   // Dev aid: if a real record maps to nothing, show its keys once so PROFILE_FIELDS can be fixed
//   if (process.env.NODE_ENV !== 'production' && raw && !warnedUnmappedProfile
//       && Object.values(p).every((v) => !v)) {
//     warnedUnmappedProfile = true;
//     console.warn('[CertificateLeaderboard] Profile fields not matched. Record keys:', Object.keys(raw));
//   }
//   return p;
// };

// /**
//  * Same certificate rules as CompanyCertificates, but returns structured certs
//  * so we can filter by category and count by level.
//  * Each cert: { title, level (1-5), levelName, type, category|null, sub|null }
//  *   type 'catSub'   → category + subcategory
//  *   type 'crossSub' → subcategory across >1 category ("Leadership - X"), category = null
//  *   type 'catTotal' → whole category across >1 subcategory
//  */
// const computeUserCerts = (userTests) => {
//   const byCatSub = {};
//   const bySubAll = {};
//   const byCategoryTotals = {};

//   Object.values(userTests).forEach((test) => {
//     if (!test || typeof test !== 'object') return;
//     const total = parseInt(test.totalAnswered || test.qlen || 0, 10);
//     const score = parseInt(test.testscore || 0, 10);
//     const difficulty = test.difficulty || 'basic';
//     const passed = typeof test.testPassed === 'boolean'
//       ? test.testPassed
//       : calculateTestPassed(score, total, difficulty);
//     if (!passed) return;

//     const lc = Number(typeof test.lcEarned === 'number'
//       ? test.lcEarned
//       : calculateLCEarned(score, true, difficulty)) || 0;
//     const cat = test.leadCategory || test.categoryType || 'general';
//     const sub = test.leadSubCategory || '';
//     if (!sub) return;

//     byCatSub[cat] = byCatSub[cat] || {};
//     byCatSub[cat][sub] = byCatSub[cat][sub] || { lc: 0, tests: 0 };
//     byCatSub[cat][sub].lc += lc;
//     byCatSub[cat][sub].tests += 1;

//     bySubAll[sub] = bySubAll[sub] || { lc: 0, tests: 0, categories: new Set() };
//     bySubAll[sub].lc += lc;
//     bySubAll[sub].tests += 1;
//     bySubAll[sub].categories.add(cat);

//     byCategoryTotals[cat] = byCategoryTotals[cat] || { lc: 0, tests: 0, subs: new Set() };
//     byCategoryTotals[cat].lc += lc;
//     byCategoryTotals[cat].tests += 1;
//     byCategoryTotals[cat].subs.add(sub);
//   });

//   const certs = new Map(); // title -> cert (dedupe, mirrors the old Set)
//   const add = (lvl, type, category, sub, title) => {
//     if (lvl > 0 && !certs.has(title)) {
//       certs.set(title, { title, level: lvl, levelName: LEVELS[lvl - 1], type, category, sub });
//     }
//   };

//   Object.entries(byCatSub).forEach(([cat, subs]) => {
//     const clean = cleanMainCategory(cat);
//     Object.entries(subs).forEach(([sub, info]) => {
//       if (info.tests < MIN_TESTS_FOR_CERT) return;
//       const lvl = computeLevel(info.lc);
//       add(lvl, 'catSub', clean, sub,
//         `Real-Time ${LEVELS[lvl - 1]} Certificate In ${clean} ${sub}`);
//     });
//   });

//   Object.entries(bySubAll).forEach(([sub, info]) => {
//     if (info.categories.size <= 1 || info.tests < MIN_TESTS_FOR_CERT) return;
//     const lvl = computeLevel(info.lc);
//     add(lvl, 'crossSub', null, sub,
//       `Real-Time ${LEVELS[lvl - 1]} Certificate In Leadership - ${sub}`);
//   });

//   Object.entries(byCategoryTotals).forEach(([cat, info]) => {
//     if (info.subs.size <= 1 || info.tests < MIN_TESTS_FOR_CERT) return;
//     const clean = cleanMainCategory(cat);
//     const lvl = computeLevel(info.lc);
//     add(lvl, 'catTotal', clean, null,
//       `Real-Time ${LEVELS[lvl - 1]} Certificate In ${clean} Leadership`);
//   });

//   return [...certs.values()];
// };

// // counts[0] = Pinnacle ... counts[4] = Foundational
// const countByLevel = (certs) => {
//   const counts = [0, 0, 0, 0, 0];
//   certs.forEach((c) => { counts[5 - c.level] += 1; });
//   return counts;
// };

// // Lexicographic: most Pinnacle wins, then Elite, ... then total, then name.
// const compareRows = (a, b) => {
//   for (let i = 0; i < 5; i++) {
//     if (b.counts[i] !== a.counts[i]) return b.counts[i] - a.counts[i];
//   }
//   if (b.total !== a.total) return b.total - a.total;
//   return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
// };

// /* ------------------------------------------------------------------ */
// /* Component                                                           */
// /* ------------------------------------------------------------------ */

// function CertificateLeaderboard({ userdata, reAuthKey }) {
//   const [companies, setCompanies] = useState([]);           // [{ id, name }]
//   const [selectedCompany, setSelectedCompany] = useState('all');
//   const [selectedCategory, setSelectedCategory] = useState('all');
//   const [selectedPosition, setSelectedPosition] = useState('all');
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [dataVersion, setDataVersion] = useState(0);         // bumps when cache fills
//   const [expanded, setExpanded] = useState({});              // { Pinnacle: true, ... }

//   // Raw data cache — filter changes never re-hit Firebase.
//   // companies[cid] = { [uid]: certs[] }, profiles[uid] = normalized profile
//   const cacheRef = useRef({ reAuthKey: null, companies: {}, profiles: {} });

//   // Rows are keyed by userID (the userTestResults key), so match on that
//   const currentUid = userdata?.userID || null;

//   /* ---- Load companies -------------------------------------------- */
//   useEffect(() => {
//     if (!userdata) return;
//     firebase.database.ref('active-companies').once('value')
//       .then((snap) => {
//         const comps = [];
//         snap.forEach((child) => {
//           const val = child.val();
//           if (val?.companyID && val?.companyName) comps.push({ id: val.companyID, name: val.companyName });
//         });
//         comps.sort((a, b) => a.name.localeCompare(b.name));
//         setCompanies(comps);
//       })
//       .catch((err) => {
//         console.error('Error loading companies:', err);
//         setError('Could not load companies.');
//       });
//   }, [userdata, reAuthKey]);

//   /* ---- Load test results + profiles for the selected scope -------- */
//   useEffect(() => {
//     console.log('myresume userdata: ', userdata);

//     if (!userdata || companies.length === 0) return undefined;
//     let cancelled = false;
//     const cache = cacheRef.current;

//     // Fresh auth → drop anything cached under the old session
//     if (cache.reAuthKey !== reAuthKey) {
//       cache.reAuthKey = reAuthKey;
//       cache.companies = {};
//       cache.profiles = {};
//     }

//     const run = async () => {
//       setLoading(true);
//       setError(null);

//       const targetIds = selectedCompany === 'all' ? companies.map((c) => c.id) : [selectedCompany];
//       const missingCompanies = targetIds.filter((cid) => !cache.companies[cid]);

//       await Promise.all(missingCompanies.map(async (cid) => {
//         try {
//           const snap = await firebase.database.ref(`userTestResults/${cid}`).once('value');
//           const users = {};
//           Object.entries(snap.val() || {}).forEach(([uid, tests]) => {
//             if (!tests || typeof tests !== 'object') return;
//             const certs = computeUserCerts(tests);
//             if (certs.length) users[uid] = certs;
//           });
//           cache.companies[cid] = users;
//         } catch (err) {
//           console.error(`Error reading company ${cid}:`, err);
//           cache.companies[cid] = {};
//         }
//       }));

//       // Profiles only for users who actually hold certificates
//       const uids = new Set();
//       targetIds.forEach((cid) => Object.keys(cache.companies[cid] || {}).forEach((u) => uids.add(u)));
//       const missingProfiles = [...uids].filter((u) => !(u in cache.profiles));

//       for (let i = 0; i < missingProfiles.length; i += PROFILE_FETCH_BATCH) {
//         if (cancelled) return;
//         const batch = missingProfiles.slice(i, i + PROFILE_FETCH_BATCH);
//         // eslint-disable-next-line no-await-in-loop
//         await Promise.all(batch.map(async (uid) => {
//           try {
//             const snap = await firebase.database.ref(PROFILE_PATH(uid)).once('value');
//             cache.profiles[uid] = normalizeProfile(snap.val());
//           } catch (err) {
//             cache.profiles[uid] = normalizeProfile(null);
//           }
//         }));
//       }

//       if (!cancelled) {
//         setDataVersion((v) => v + 1);
//         setLoading(false);
//       }
//     };

//     run();
//     return () => { cancelled = true; };
//   }, [userdata, companies, selectedCompany, reAuthKey]);

//   /* ---- Base rows: everyone in the selected company scope ----------- */
//   const baseRows = useMemo(() => {
//     const cache = cacheRef.current;
//     const nameById = Object.fromEntries(companies.map((c) => [c.id, c.name]));
//     const targetIds = selectedCompany === 'all' ? companies.map((c) => c.id) : [selectedCompany];
//     const rows = [];
//     targetIds.forEach((cid) => {
//       Object.entries(cache.companies[cid] || {}).forEach(([uid, certs]) => {
//         const profile = cache.profiles[uid] || normalizeProfile(null);
//         rows.push({
//           key: `${cid}:${uid}`,
//           uid,
//           companyID: cid,
//           company: nameById[cid] || '',
//           allCerts: certs,
//           ...profile,
//         });
//       });
//     });
//     return rows;
//   }, [dataVersion, companies, selectedCompany]);

//   /* ---- Filter options (derived from the company scope) ------------ */
//   const categoryOptions = useMemo(() => {
//     const set = new Set();
//     baseRows.forEach((r) => r.allCerts.forEach((c) => { if (c.category) set.add(c.category); }));
//     return [...set].sort((a, b) => a.localeCompare(b));
//   }, [baseRows]);

//   const positionOptions = useMemo(() => {
//     const set = new Set();
//     baseRows.forEach((r) => { if (r.position) set.add(r.position); });
//     return [...set].sort((a, b) => a.localeCompare(b));
//   }, [baseRows]);

//   // Reset a filter if its value vanished after switching company
//   useEffect(() => {
//     if (selectedCategory !== 'all' && !categoryOptions.includes(selectedCategory)) setSelectedCategory('all');
//   }, [categoryOptions, selectedCategory]);
//   useEffect(() => {
//     if (selectedPosition !== 'all' && !positionOptions.includes(selectedPosition)) setSelectedPosition('all');
//   }, [positionOptions, selectedPosition]);

//   /* ---- Ranked + sectioned rows ------------------------------------ */
//   const { sections, rankedCount } = useMemo(() => {
//     const ranked = baseRows
//       .filter((r) => selectedPosition === 'all' || r.position === selectedPosition)
//       .map((r) => {
//         // Category filter: only certs tied to that category count.
//         // Cross-category "Leadership - X" certs have no single category, so they drop out.
//         const certs = selectedCategory === 'all'
//           ? r.allCerts
//           : r.allCerts.filter((c) => c.category === selectedCategory);
//         const counts = countByLevel(certs);
//         const highestIdx = counts.findIndex((n) => n > 0); // 0 = Pinnacle
//         return { ...r, certs, counts, total: certs.length, highest: SECTION_ORDER[highestIdx] };
//       })
//       .filter((r) => r.total > 0)
//       .sort(compareRows)
//       .map((r, i) => ({ ...r, rank: i + 1 }));

//     const grouped = Object.fromEntries(SECTION_ORDER.map((lvl) => [lvl, []]));
//     ranked.forEach((r) => grouped[r.highest].push(r));
//     return { sections: grouped, rankedCount: ranked.length };
//   }, [baseRows, selectedCategory, selectedPosition]);

//   /* ---- Render ------------------------------------------------------ */
//   if (!userdata) return <Box sx={{ p: 2 }}>Please sign in.</Box>;

//   const scopeLabel = [
//     selectedCategory !== 'all' ? selectedCategory : 'All categories',
//     selectedCompany !== 'all' ? companies.find((c) => c.id === selectedCompany)?.name : 'All companies',
//     selectedPosition !== 'all' ? selectedPosition : null,
//   ].filter(Boolean).join(' · ');

//   const hideOnMobile = { display: { xs: 'none', md: 'table-cell' } };
//   const COLS = 8;

//   const renderFilter = (label, value, onChange, options, allLabel, getKey = (o) => o, getLabel = (o) => o) => (
//     <FormControl size="small" sx={{ minWidth: 200, flex: '1 1 200px' }} disabled={loading}>
//       <InputLabel>{label}</InputLabel>
//       <Select label={label} value={value} onChange={(e) => onChange(e.target.value)}>
//         <MenuItem value="all">{allLabel}</MenuItem>
//         {options.map((o) => <MenuItem key={getKey(o)} value={getKey(o)}>{getLabel(o)}</MenuItem>)}
//       </Select>
//     </FormControl>
//   );

//   const renderCertCell = (row) => (
//     <Tooltip
//       arrow
//       placement="left"
//       title={
//         <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
//           {[...row.certs].sort((a, b) => b.level - a.level).map((c) => (
//             <div key={c.title} style={{ fontSize: 12, marginBottom: 4 }}>{c.title}</div>
//           ))}
//         </Box>
//       }
//     >
//       <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
//         {SECTION_ORDER.map((lvl, i) => (row.counts[i] > 0 ? (
//           <Chip
//             key={lvl}
//             size="small"
//             label={`${lvl.charAt(0)} ${row.counts[i]}`}
//             sx={{ bgcolor: LEVEL_COLORS[lvl].bg, color: LEVEL_COLORS[lvl].main, fontWeight: 600, height: 22 }}
//           />
//         ) : null))}
//         <Typography component="span" sx={{ fontWeight: 700, ml: 0.5, minWidth: 24, textAlign: 'right' }}>
//           {row.total}
//         </Typography>
//       </Box>
//     </Tooltip>
//   );

//   return (
//     <Box sx={{ p: { xs: 1.5, md: 2.5 }, maxWidth: 1200, mx: 'auto' }}>
//       <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Certificate Leaderboard</Typography>
//       <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
//         {scopeLabel}{!loading && ` — ${rankedCount} ${rankedCount === 1 ? 'person' : 'people'} ranked`}
//       </Typography>

//       <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
//         {renderFilter('Category', selectedCategory, setSelectedCategory, categoryOptions, 'All categories')}
//         {renderFilter('Company', selectedCompany, setSelectedCompany, companies, 'All companies', (c) => c.id, (c) => c.name)}
//         {renderFilter('Position', selectedPosition, setSelectedPosition, positionOptions, 'All positions')}
//       </Box>

//       {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

//       {loading ? (
//         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4, color: 'text.secondary' }}>
//           <CircularProgress size={20} /> Loading leaderboard…
//         </Box>
//       ) : rankedCount === 0 ? (
//         <Typography color="text.secondary" sx={{ py: 4 }}>
//           No certificates match this selection yet.
//         </Typography>
//       ) : (
//         <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '75vh' }}>
//           <Table stickyHeader size="small">
//             <TableHead>
//               <TableRow>
//                 <TableCell sx={{ width: 48 }}>#</TableCell>
//                 <TableCell sx={{ width: 56 }} />
//                 <TableCell>Name</TableCell>
//                 <TableCell>Last name</TableCell>
//                 <TableCell sx={hideOnMobile}>Sector</TableCell>
//                 <TableCell sx={hideOnMobile}>Country</TableCell>
//                 <TableCell sx={hideOnMobile}>Company</TableCell>
//                 <TableCell align="right">Certificates</TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {SECTION_ORDER.map((lvl) => {
//                 const rows = sections[lvl];
//                 if (!rows.length) return null;
//                 const showAll = expanded[lvl];
//                 const visible = showAll ? rows : rows.slice(0, SECTION_PAGE_SIZE);
//                 const colors = LEVEL_COLORS[lvl];

//                 return (
//                   <React.Fragment key={lvl}>
//                     <TableRow>
//                       <TableCell
//                         colSpan={COLS}
//                         sx={{
//                           bgcolor: colors.bg, color: colors.main, fontWeight: 700, fontSize: 15,
//                           borderTop: `2px solid ${colors.main}`, position: 'sticky', top: 37, zIndex: 1,
//                         }}
//                       >
//                         {lvl}
//                         <Typography component="span" sx={{ ml: 1.5, fontSize: 13, color: 'text.secondary' }}>
//                           {rows.length} {rows.length === 1 ? 'person' : 'people'}
//                         </Typography>
//                       </TableCell>
//                     </TableRow>

//                     {visible.map((r) => {
//                       const isMe = currentUid && r.uid === currentUid;
//                       return (
//                         <TableRow key={r.key} hover sx={isMe ? { bgcolor: '#fef9c3' } : undefined}>
//                           <TableCell sx={{ fontWeight: 700, color: r.rank <= 3 ? colors.main : 'text.secondary' }}>
//                             {r.rank}
//                           </TableCell>
//                           <TableCell>
//                             <Avatar src={r.avatar || undefined} sx={{ width: 32, height: 32, fontSize: 14 }}>
//                               {(r.firstName || '?').charAt(0).toUpperCase()}
//                             </Avatar>
//                           </TableCell>
//                           <TableCell>{r.firstName || '—'}{isMe && ' (you)'}</TableCell>
//                           <TableCell>{r.lastName || '—'}</TableCell>
//                           <TableCell sx={hideOnMobile}>{r.sector || '—'}</TableCell>
//                           <TableCell sx={hideOnMobile}>{r.country || '—'}</TableCell>
//                           <TableCell sx={hideOnMobile}>{r.company || '—'}</TableCell>
//                           <TableCell align="right">{renderCertCell(r)}</TableCell>
//                         </TableRow>
//                       );
//                     })}

//                     {rows.length > SECTION_PAGE_SIZE && (
//                       <TableRow>
//                         <TableCell colSpan={COLS} align="center">
//                           <Button size="small" onClick={() => setExpanded((e) => ({ ...e, [lvl]: !showAll }))}>
//                             {showAll ? `Show top ${SECTION_PAGE_SIZE}` : `Show all ${rows.length}`}
//                           </Button>
//                         </TableCell>
//                       </TableRow>
//                     )}
//                   </React.Fragment>
//                 );
//               })}
//             </TableBody>
//           </Table>
//         </TableContainer>
//       )}
//     </Box>
//   );
// }

// const mapStateToProps = (state) => ({
//   userdata: state.userDialog.userDetails[0] || null,
//   reAuthKey: state.reAuth?.reAuthKey || 0,
// });

// export default connect(mapStateToProps)(CertificateLeaderboard);
//////////////////////////////////////////////////////////////////////////////////////////
// import React, { useEffect, useMemo, useState } from 'react';
// import { connect } from 'react-redux';
// import firebase from '../store/firebase';
// import { calculateTestPassed, calculateLCEarned } from '../utils/test_utils';

// function CompanyCertificates({ userdata, reAuthKey }) {  
//   const [loading, setLoading] = useState(true);
//   const [companies, setCompanies] = useState([]); // [{ id, name }]
//   const [selectedCompany, setSelectedCompany] = useState('all');
//   const [companyCertificates, setCompanyCertificates] = useState({}); // { title: count }

//   // Thresholds (same as before)
//   const LEVEL_THRESHOLDS = 
//     userdata?.email === 'adriaan@leadershipplatform.com'
//       ? [3, 10, 18, 30, 45]
//       : [5, 10, 18, 30, 45];

//   const MIN_TESTS_FOR_CERT = 1;

//   const cleanMainCategory = (name) => {
//     if (!name) return '';
//     const parts = String(name).split(/\s*[–—-]\s*/);
//     return parts[0].trim();
//   };

//   const computeLevel = (lc) => {
//     if (!lc || lc <= 0) return 0;
//     let lvl = 0;
//     for (let i = 0; i < LEVEL_THRESHOLDS.length; i++) {
//       if (lc >= LEVEL_THRESHOLDS[i]) lvl = i + 1;
//     }
//     return lvl;
//   };

//   const getLevelName = (level) => {
//     switch (level) {
//       case 1: return 'Foundational';
//       case 2: return 'Advanced';
//       case 3: return 'Distinguished';
//       case 4: return 'Elite';
//       case 5: return 'Pinnacle';
//       default: return '';
//     }
//   };

//   // Extract level from certificate title
//   const getLevelFromTitle = (title) => {
//     if (title.includes('Foundational'))   return 'Foundational';
//     if (title.includes('Advanced'))       return 'Advanced';
//     if (title.includes('Distinguished'))  return 'Distinguished';
//     if (title.includes('Elite'))          return 'Elite';
//     if (title.includes('Pinnacle'))       return 'Pinnacle';
//     return 'Other';
//   };

//   // Load list of companies
//   useEffect(() => {
//     if (!userdata) return;

//     const companiesRef = firebase.database.ref('active-companies');
//     companiesRef.once('value', (snap) => {
//       const comps = [];
//       snap.forEach((child) => {
//         const val = child.val();
//         if (val?.companyID && val?.companyName) {
//           comps.push({
//             id: val.companyID,
//             name: val.companyName,
//           });
//         }
//       });
//       comps.sort((a, b) => a.name.localeCompare(b.name));
//       setCompanies(comps);

//       const userCompanyID = userdata.real_companyID || userdata.companyID;
//       if (userCompanyID && comps.some(c => c.id === userCompanyID)) {
//         setSelectedCompany(userCompanyID);
//       }
//     }).catch(err => {
//       console.error('Error loading companies:', err);
//     });
//   }, [userdata, reAuthKey]);  // ← ADD reAuthKey

//   // Load & compute certificates
//   useEffect(() => {
//     if (!userdata || companies.length === 0) return;

//     setLoading(true);
//     setCompanyCertificates({});

//     const processUserTests = (userTests) => {
//       const passedTests = [];

//       Object.entries(userTests).forEach(([surveyid, test]) => {
//         const total = parseInt(test.totalAnswered || test.qlen || 0);
//         const score = parseInt(test.testscore || 0);
//         const passedBool =
//           typeof test.testPassed === 'boolean'
//             ? test.testPassed
//             : calculateTestPassed(score, total, test.difficulty || 'basic');

//         if (!passedBool) return;

//         const lc =
//           typeof test.lcEarned === 'number'
//             ? test.lcEarned
//             : passedBool
//               ? calculateLCEarned(score, true, test.difficulty || 'basic')
//               : 0;

//         passedTests.push({
//           ...test,
//           surveyid,
//           lc,
//           leadCategory: test.leadCategory || test.categoryType || 'general',
//           leadSubCategory: test.leadSubCategory || '',
//         });
//       });

//       if (passedTests.length === 0) return new Set();

//       const byCatSub = {};
//       const bySubAll = {};
//       const byCategoryTotals = {};

//       passedTests.forEach((r) => {
//         const cat = r.leadCategory;
//         const sub = r.leadSubCategory;
//         const lc = Number(r.lc || 0);

//         if (!sub) return;

//         byCatSub[cat] = byCatSub[cat] || {};
//         byCatSub[cat][sub] = byCatSub[cat][sub] || { lc: 0, tests: 0 };
//         byCatSub[cat][sub].lc += lc;
//         byCatSub[cat][sub].tests += 1;

//         bySubAll[sub] = bySubAll[sub] || { lc: 0, tests: 0, categories: new Set() };
//         bySubAll[sub].lc += lc;
//         bySubAll[sub].tests += 1;
//         bySubAll[sub].categories.add(cat);

//         byCategoryTotals[cat] = byCategoryTotals[cat] || { lc: 0, tests: 0, subs: new Set() };
//         byCategoryTotals[cat].lc += lc;
//         byCategoryTotals[cat].tests += 1;
//         byCategoryTotals[cat].subs.add(sub);
//       });

//       const userCerts = new Set();

//       Object.keys(byCatSub).forEach((cat) => {
//         Object.keys(byCatSub[cat]).forEach((sub) => {
//           const info = byCatSub[cat][sub];
//           if (info.tests >= MIN_TESTS_FOR_CERT) {
//             const lvl = computeLevel(info.lc);
//             if (lvl > 0) {
//               const title = `Real-Time ${getLevelName(lvl)} Certificate In ${cleanMainCategory(cat)} ${sub}`;
//               userCerts.add(title);
//             }
//           }
//         });
//       });

//       Object.keys(bySubAll).forEach((sub) => {
//         const info = bySubAll[sub];
//         if (info.categories.size > 1 && info.tests >= MIN_TESTS_FOR_CERT) {
//           const lvl = computeLevel(info.lc);
//           if (lvl > 0) {
//             const title = `Real-Time ${getLevelName(lvl)} Certificate In Leadership - ${sub}`;
//             userCerts.add(title);
//           }
//         }
//       });

//       Object.keys(byCategoryTotals).forEach((cat) => {
//         const info = byCategoryTotals[cat];
//         if (info.subs.size > 1 && info.tests >= MIN_TESTS_FOR_CERT) {
//           const lvl = computeLevel(info.lc);
//           if (lvl > 0) {
//             const title = `Real-Time ${getLevelName(lvl)} Certificate In ${cleanMainCategory(cat)} Leadership`;
//             userCerts.add(title);
//           }
//         }
//       });

//       return userCerts;
//     };

//     const loadData = async () => {
//       const certCounts = {};

//       if (selectedCompany === 'all') {
//         for (const comp of companies) {
//           const ref = firebase.database.ref(`userTestResults/${comp.id}`);
//           try {
//             const snap = await ref.once('value');
//             const companyData = snap.val() || {};

//             for (const userTests of Object.values(companyData)) {
//               if (!userTests || typeof userTests !== 'object') continue;
//               const userCerts = processUserTests(userTests);
//               userCerts.forEach(title => {
//                 certCounts[title] = (certCounts[title] || 0) + 1;
//               });
//             }
//           } catch (err) {
//             console.error(`Error reading company ${comp.id}:`, err);
//           }
//         }
//       } else {
//         const ref = firebase.database.ref(`userTestResults/${selectedCompany}`);
//         try {
//           const snap = await ref.once('value');
//           const companyData = snap.val() || {};

//           for (const userTests of Object.values(companyData)) {
//             if (!userTests || typeof userTests !== 'object') continue;
//             const userCerts = processUserTests(userTests);
//             userCerts.forEach(title => {
//               certCounts[title] = (certCounts[title] || 0) + 1;
//             });
//           }
//         } catch (err) {
//           console.error(`Error reading company ${selectedCompany}:`, err);
//         }
//       }

//       // Sort: most people first, then alphabetically
//       const sortedEntries = Object.entries(certCounts).sort((a, b) => {
//         if (b[1] !== a[1]) return b[1] - a[1];
//         return a[0].localeCompare(b[0]);
//       });

//       setCompanyCertificates(Object.fromEntries(sortedEntries));
//       setLoading(false);
//     };

//     loadData();
//   }, [userdata, selectedCompany, companies, reAuthKey]);

//   // Group certificates by level
//   const levelGroups = useMemo(() => {
//     const groups = {
//       Pinnacle:      { titles: [], totalPeople: 0 },
//       Elite:         { titles: [], totalPeople: 0 },
//       Distinguished: { titles: [], totalPeople: 0 },
//       Advanced:      { titles: [], totalPeople: 0 },
//       Foundational:  { titles: [], totalPeople: 0 },
//       Other:         { titles: [], totalPeople: 0 },
//     };

//     Object.entries(companyCertificates).forEach(([title, count]) => {
//       const level = getLevelFromTitle(title);
//       groups[level].titles.push({ title, count });
//       groups[level].totalPeople += count;
//     });

//     // Sort titles within each level by popularity
//     Object.values(groups).forEach(group => {
//       group.titles.sort((a, b) => b.count - a.count);
//     });

//     return groups;
//   }, [companyCertificates]);

//   if (!userdata) {
//     return <div style={{ padding: 16 }}>Please sign in.</div>;
//   }

//   const companyName =
//     selectedCompany === 'all'
//       ? 'All Companies'
//       : companies.find(c => c.id === selectedCompany)?.name || 'Selected Company';

//   const totalUnique = Object.keys(companyCertificates).length;
//   const totalEarned = Object.values(companyCertificates).reduce((sum, c) => sum + c, 0);

//   return (
//     <div style={{ padding: '16px 20px', maxWidth: 1000, margin: '0 auto' }}>
//       <h2 style={{ margin: '0 0 24px', fontSize: '24px' }}>
//         Certificates Earned – {companyName}
//         {totalUnique > 0 && (
//           <span style={{ color: '#555', fontSize: '18px', marginLeft: '12px' }}>
//             ({totalUnique} unique • {totalEarned} total earned)
//           </span>
//         )}
//       </h2>

//       {/* Company selector */}
//       <div style={{ marginBottom: 32, maxWidth: 400 }}>
//         <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold' }}>
//           Select Company
//         </label>
//         <select
//           value={selectedCompany}
//           onChange={(e) => setSelectedCompany(e.target.value)}
//           style={{
//             width: '100%',
//             padding: '10px',
//             fontSize: '16px',
//             borderRadius: '6px',
//             border: '1px solid #ccc',
//           }}
//         >
//           <option value="all">All Companies</option>
//           {companies.map(comp => (
//             <option key={comp.id} value={comp.id}>
//               {comp.name}
//             </option>
//           ))}
//         </select>
//       </div>

//       {loading ? (
//         <div style={{ fontSize: '16px', color: '#666' }}>Loading certificates...</div>
//       ) : totalUnique === 0 ? (
//         <div style={{ color: '#777', fontSize: '16px' }}>
//           No certificates have been earned {selectedCompany === 'all' ? 'across any companies' : 'in this company'} yet.
//         </div>
//       ) : (
//         <div style={{ display: 'flex', flexDirection: 'column', gap: 40 }}>
//           {['Pinnacle', 'Elite', 'Distinguished', 'Advanced', 'Foundational'].map(level => {
//             const group = levelGroups[level];
//             if (group.titles.length === 0) return null;

//             return (
//               <div key={level}>
//                 <h3 style={{
//                   fontSize: '20px',
//                   margin: '0 0 16px',
//                   color: '#1e3a8a',
//                   paddingBottom: '8px',
//                   borderBottom: '2px solid #dbeafe',
//                 }}>
//                   {level} Level
//                   <span style={{ fontSize: '16px', color: '#4b5563', marginLeft: '16px' }}>
//                     ({group.titles.length} unique titles • {group.totalPeople} people)
//                   </span>
//                 </h3>

//                 <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
//                   {group.titles.map(({ title, count }, index) => (
//                     <li
//                       key={title}
//                       style={{
//                         marginBottom: '12px',
//                         padding: '12px 16px',
//                         background: index % 2 === 0 ? '#f0f9ff' : 'white',
//                         borderRadius: '8px',
//                         border: '1px solid #bfdbfe',
//                         display: 'flex',
//                         justifyContent: 'space-between',
//                         alignItems: 'center',
//                         fontSize: '15px',
//                       }}
//                     >
//                       <div style={{ flex: 1 }}>{title}</div>
//                       <div style={{
//                         minWidth: '100px',
//                         textAlign: 'right',
//                         fontWeight: 'bold',
//                         color: '#1d4ed8',
//                       }}>
//                         {count} {count === 1 ? 'person' : 'people'}
//                       </div>
//                     </li>
//                   ))}
//                 </ul>
//               </div>
//             );
//           })}

//           {/* Show "Other" only if it exists */}
//           {levelGroups.Other.titles.length > 0 && (
//             <div>
//               <h3 style={{
//                 fontSize: '18px',
//                 margin: '0 0 16px',
//                 color: '#4b5563',
//               }}>
//                 Other / Unclassified
//               </h3>
//               <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
//                 {levelGroups.Other.titles.map(({ title, count }, index) => (
//                   <li
//                     key={title}
//                     style={{
//                       marginBottom: '10px',
//                       padding: '10px 14px',
//                       background: index % 2 === 0 ? '#f3f4f6' : 'white',
//                       borderRadius: '6px',
//                       border: '1px solid #d1d5db',
//                       display: 'flex',
//                       justifyContent: 'space-between',
//                       fontSize: '15px',
//                     }}
//                   >
//                     <div style={{ flex: 1 }}>{title}</div>
//                     <div style={{ minWidth: '100px', textAlign: 'right', fontWeight: 'bold' }}>
//                       {count} {count === 1 ? 'person' : 'people'}
//                     </div>
//                   </li>
//                 ))}
//               </ul>
//             </div>
//           )}
//         </div>
//       )}
//     </div>
//   );
// }

// const mapStateToProps = (state) => ({
//   userdata: state.userDialog.userDetails[0] || null,
//   reAuthKey: state.reAuth?.reAuthKey || 0,  
// });

// export default connect(mapStateToProps)(CompanyCertificates);
